import { useState, useEffect, useCallback } from 'react';
import { fetchOrdini, fetchRegioni } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PROVINCE_OPTIONS = [
  'AG', 'AL', 'AN', 'AO', 'AR', 'AP', 'AT', 'AV', 'BA', 'BT', 'BL', 'BN', 'BG', 'BI', 'BO', 'BZ',
  'BS', 'BR', 'CA', 'CL', 'CB', 'CE', 'CT', 'CZ', 'CH', 'CO', 'CS', 'CR', 'KR', 'CN', 'EN', 'FM',
  'FE', 'FI', 'FG', 'FC', 'FR', 'GE', 'GO', 'GR', 'IM', 'IS', 'SP', 'AQ', 'LT', 'LE', 'LC', 'LI',
  'LO', 'LU', 'MC', 'MN', 'MS', 'MT', 'ME', 'MI', 'MO', 'MB', 'NA', 'NO', 'NU', 'OR', 'PD', 'PA',
  'PR', 'PV', 'PG', 'PU', 'PE', 'PC', 'PI', 'PT', 'PN', 'PZ', 'PO', 'RG', 'RA', 'RC', 'RE', 'RI',
  'RN', 'RM', 'RO', 'SA', 'SS', 'SV', 'SI', 'SR', 'SO', 'TA', 'TE', 'TR', 'TO', 'TP', 'TN',
  'TV', 'TS', 'UD', 'VA', 'VE', 'VB', 'VC', 'VR', 'VV', 'VI', 'VT'
];

export default function OrdiniPage() {
  const [ordini, setOrdini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [provincia, setProvincia] = useState('all');
  const [regione, setRegione] = useState('all');
  const [regioni, setRegioni] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const loadRegioni = async () => {
      try {
        const data = await fetchRegioni();
        setRegioni(data);
      } catch (error) {
        console.error('Error loading regioni:', error);
      }
    };
    loadRegioni();
  }, []);

  const loadOrdini = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchOrdini({ search, provincia, regione, page, limit: 20 });
      setOrdini(data.items);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch (error) {
      console.error('Error loading ordini:', error);
    } finally {
      setLoading(false);
    }
  }, [search, provincia, regione, page]);

  useEffect(() => {
    loadOrdini();
  }, [loadOrdini]);

  useEffect(() => {
    setPage(1);
  }, [search, provincia, regione]);

  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'attiva':
        return <span className="badge-success">Completa</span>;
      default:
        return <span className="badge-warning">Incompleta</span>;
    }
  };

  const handleRowClick = (ordine) => {
    navigate(`/deleghe?ordine=${ordine.id}`);
  };

  return (
    <div className="p-6 lg:p-8" data-testid="ordini-page">
      {/* Header */}
      <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">Ordini Territoriali</h1>
            <p className="text-slate-500 mt-1">{total} ordini registrati</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-institutional mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Cerca per denominazione, città..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={regione} onValueChange={(val) => { setRegione(val); setProvincia('all'); }}>
              <SelectTrigger className="w-full sm:w-48" data-testid="regione-select">
                <SelectValue placeholder="Tutte le regioni" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le regioni</SelectItem>
                {regioni.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={provincia} onValueChange={(val) => setProvincia(val)}>
              <SelectTrigger className="w-full sm:w-36" data-testid="provincia-select">
                <SelectValue placeholder="Provincia" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {PROVINCE_OPTIONS.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(search || provincia !== 'all' || regione !== 'all') && (
              <Button 
                variant="outline" 
                onClick={() => { setSearch(''); setProvincia('all'); setRegione('all'); }}
                data-testid="clear-filters-btn"
              >
                Pulisci filtri
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="card-institutional">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="spinner mx-auto" />
            </div>
          ) : ordini.length === 0 ? (
            <div className="empty-state py-12">
              <Building2 className="empty-state-icon" />
              <p className="empty-state-title">Nessun ordine trovato</p>
              <p className="empty-state-description">
                Modifica i filtri di ricerca per visualizzare i risultati
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="data-table" data-testid="ordini-table">
                <thead>
                  <tr>
                    <th>Provincia</th>
                    <th>Denominazione</th>
                    <th>Regione</th>
                    <th>Stato Delega</th>
                  </tr>
                </thead>
                <tbody>
                  {ordini.map((ordine) => (
                    <tr 
                      key={ordine.id} 
                      className="cursor-pointer"
                      onClick={() => handleRowClick(ordine)}
                      data-testid={`ordine-row-${ordine.sigla_provincia}`}
                    >
                      <td>
                        <div>
                          <span className="font-medium text-slate-900">{ordine.sigla_provincia}</span>
                          <p className="text-xs text-slate-500">{ordine.citta}</p>
                        </div>
                      </td>
                      <td className="max-w-xs truncate">{ordine.denominazione}</td>
                      <td className="text-sm">{ordine.regione}</td>
                      <td>{getStatoBadge(ordine.delega_stato)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                Pagina {page} di {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
